import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import SignaturePad from "signature_pad";
import ConsentModal from "../components/ConsentModal";
import { submitConsent } from "../utils/api";

const featureCards = [
  { icon: "/assets/img/Icons/Bio_Bank.svg", title: "About the Biobank", text: "A biobank stores blood and other human samples—like urine, tissue, or DNA—along with coded health information to help researchers better understand, prevent, and treat diseases.", color: "#f1eeff" },
  { icon: "/assets/img/Icons/Ethics.svg", title: "What You're Donating", text: "Samples can include blood, leftover tissue from surgery, urine, saliva, or similar materials. They are securely labeled with a code (not your name) and stored for approved research.", color: "#ebffe4" },
  { icon: "/assets/img/Icons/Sample_collection.svg", title: "How Your Samples Are Used", text: "Approved academic, nonprofit, and commercial researchers may use de-identified samples and data to discover disease causes, new tests, and potential treatments.", color: "#fff7f3" },
  { icon: "/assets/img/Icons/Privacy_and_Security.svg", title: "Privacy & Security", text: "Your name and contact details are never shared. All data are stored with multi-layer security and only coded information is provided to researchers to protect your identity.", color: "#f1eeff" },
  { icon: "/assets/img/Icons/Benefits.svg", title: "Possible Benefits", text: "You will not receive direct medical benefit or financial compensation. However, your participation may help develop future diagnostics and therapies that benefit others.", color: "#ebffe4" },
  { icon: "/assets/img/Icons/Risk_and_Difficulty.svg", title: "Risks & Discomfort", text: "No extra medical procedures are required beyond routine care. If blood is collected, you may feel mild, brief discomfort similar to a normal blood draw.", color: "#fff7f3" },
  { icon: "/assets/img/Icons/Patient_Rights.svg", title: "Your Rights", text: "Participation is completely voluntary. You may withdraw at any time in writing without affecting your medical care. Samples already analyzed or fully anonymized may not be retrievable.", color: "#f1eeff" },
  { icon: "/assets/img/Icons/Future.svg", title: "Results & Future Contact", text: "You generally will not receive research results. If a finding is medically important and actionable, you or your doctor may be notified. You may also be contacted for future studies.", color: "#ebffe4" },
  { icon: "/assets/img/Icons/Research.svg", title: "Oversight & Use of Funds", text: "Any revenue from research partnerships supports the biobank's mission and related research programs. All use of your samples is reviewed and approved to ensure ethical standard.", color: "#fff7f3" },
];

const statsData = [
  { value: "50K+", label: "Samples Stored", icon: "🧬" },
  { value: "200+", label: "Research Partners", icon: "🏛️" },
  { value: "99.9%", label: "Security Rating", icon: "🔒" },
  { value: "15+", label: "Years of Service", icon: "⏱️" },
];

const bioSpecimens = [
  "Blood",
  "Left over surgical tissue (tumour, other) / BAL / Sputum",
  "Urine",
  "Buccal Cells (oral rinse)",
  "Saliva",
  "Skin punch biopsy",
  "Faecal",
];

function dataURLtoBlob(dataURL) {
  const byteString = atob(dataURL.split(",")[1]);
  const mimeString = dataURL.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
  return new Blob([ab], { type: mimeString });
}

export default function HomePage() {
  const canvasRef = useRef(null);
  const sigPadRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [formResult, setFormResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Google Translate: detect current language from cookie
  function getGoogleTranslateLang() {
    const match = document.cookie.match(/googtrans=\/en\/([^;]+)/);
    return match ? match[1] : "en";
  }

  // Form fields
  const [patientName, setPatientName] = useState("");
  const [consentDate, setConsentDate] = useState("");
  const [patientMobile, setPatientMobile] = useState("");
  const [patientEmail, setPatientEmail] = useState("");
  const [signatureOfParticipant, setSignatureOfParticipant] = useState("");
  const [witnessName, setWitnessName] = useState("");
  const [confirmConsent, setConfirmConsent] = useState(false);
  const [personTakingConsent, setPersonTakingConsent] = useState("");
  const [hospital, setHospital] = useState("");
  const [patientId, setPatientId] = useState("");

  // Initialize signature pad
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pad = new SignaturePad(canvas, {
      minWidth: 0.8,
      maxWidth: 2.6,
      backgroundColor: "rgb(255,255,255)",
      penColor: "#1a1a2e",
    });
    sigPadRef.current = pad;

    function resizeCanvas() {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = 280 * ratio;
      canvas.style.width = rect.width + "px";
      canvas.style.height = "280px";
      const data = pad.toData();
      pad.clear();
      pad.fromData(data);
    }
    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, []);

  // Set default date to today
  useEffect(() => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, "0");
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const yyyy = now.getFullYear();
    setConsentDate(`${dd}/${mm}/${yyyy}`);
  }, []);

  const handleCheckboxChange = () => {
    if (!consentChecked) {
      setModalOpen(true);
    } else {
      setConsentChecked(false);
    }
  };

  const handleModalAgree = () => {
    setConsentChecked(true);
    setModalOpen(false);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setConsentChecked(false);
  };

  const clearSignature = () => sigPadRef.current?.clear();
  const undoSignature = () => {
    const data = sigPadRef.current?.toData();
    if (data) { data.pop(); sigPadRef.current.fromData(data); }
  };
  const downloadSignature = () => {
    if (sigPadRef.current?.isEmpty()) { alert("Please provide a signature first."); return; }
    const link = document.createElement("a");
    link.download = "signature.png";
    link.href = canvasRef.current.toDataURL("image/png");
    link.click();
  };

  const handleSubmit = useCallback(async (isPrint) => {
    // Validate all required fields
    const missing = [];
    if (!patientName.trim()) missing.push("Name of Participant");
    if (!consentDate.trim()) missing.push("Date");
    if (!patientMobile.trim()) missing.push("Mobile No.");
    if (!patientEmail.trim()) missing.push("Email ID");
    if (!consentChecked) missing.push("Read & agree to Informed Consent");
    if (!signatureOfParticipant.trim()) missing.push("Signature confirmation (typed name)");
    if (!confirmConsent) missing.push("Consent acknowledgement checkbox");
    if (!personTakingConsent.trim()) missing.push("Person taking consent");
    if (!hospital) missing.push("Hospital / Institute");
    if (sigPadRef.current?.isEmpty()) missing.push("Signature (draw above)");

    if (missing.length > 0) {
      alert("Please complete the following required fields:\n\n• " + missing.join("\n• "));
      return;
    }

    const signatureBase64 = sigPadRef.current.toDataURL("image/png");
    const formData = new FormData();
    formData.append("ParticipantName", patientName);
    formData.append("DateOfSubmit", consentDate);
    formData.append("MobileNumber", patientMobile);
    formData.append("EmailId", patientEmail);
    formData.append("Signature", signatureBase64);
    formData.append("ImpartialWitness", witnessName || "NA");
    formData.append("PersonTakingConsent", personTakingConsent);
    formData.append("NameOfTheHospital", hospital);
    formData.append("DateOfReview", "NA");
    formData.append("MRNOrHospitalId", patientId);
    formData.append("formAction", isPrint ? "Print" : "Submit");

    const lang = getGoogleTranslateLang();
    let languageFull = "English";
    if (lang === "hi") languageFull = "hindi";
    else if (lang === "te") languageFull = "telugu";
    formData.append("Language", languageFull);

    const blob = dataURLtoBlob(signatureBase64);
    formData.append("SignatureImg", blob, "signature.png");

    setSubmitting(true);
    try {
      const result = await submitConsent(formData, isPrint);
      if (isPrint && result instanceof Blob) {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(result);
        link.download = "ConsentForm.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      setFormResult({ type: "success", message: "Thank you! Your consent has been successfully submitted." });
      window.alert("Thank you! Your consent has been successfully submitted.");
      sigPadRef.current?.clear();
      setPatientName(""); setConsentDate(""); setPatientMobile(""); setPatientEmail("");
      setSignatureOfParticipant(""); setWitnessName(""); setConfirmConsent(false);
      setPersonTakingConsent(""); setHospital(""); setPatientId("");
      setConsentChecked(false);
      // re-set today's date
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, "0");
      const mm = String(now.getMonth() + 1).padStart(2, "0");
      setConsentDate(`${dd}/${mm}/${now.getFullYear()}`);
    } catch (err) {
      setFormResult({ type: "error", message: "Unable to submit consent form. Please try again. " + err.message });
    }
    setSubmitting(false);
  }, [patientName, consentDate, patientMobile, patientEmail, signatureOfParticipant, witnessName, confirmConsent, consentChecked, personTakingConsent, hospital, patientId]);

  const onFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(false);
  };

  return (
    <div className="page-home">
      {/* Hero */}
      <section className="hero-section" style={{ backgroundImage: 'url(/assets/img/breadcrumb/thumb-13.jpg)' }}>
        <div className="hero-overlay">
          <div className="hero-content">
            <div className="hero-badge">Secure &middot; Ethical &middot; Confidential</div>
            <img src="/assets/img/logo/continentallogo.jpg" alt="Continental Hospitals" className="hero-logo" />
            <h1 className="hero-title">Continental BioBank<br /><span>INFORMED CONSENT</span></h1>
            <p className="hero-subtitle">Your participation helps advance medical research. All samples are stored securely with multi-layer protection.</p>
            <div className="hero-actions">
              <a href="#consent-form" className="btn-hero-cta">
                Provide Consent
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
              </a>
              <Link to="/survey" className="btn-hero-secondary">
                Take Health Survey
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Ticker */}
      <section className="stats-ticker">
        <div className="container">
          <div className="stats-ticker-grid">
            {statsData.map((s, i) => (
              <div className="stats-ticker-item" key={i}>
                <span className="stats-ticker-icon">{s.icon}</span>
                <div>
                  <span className="stats-ticker-value">{s.value}</span>
                  <span className="stats-ticker-label">{s.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Survey CTA Banner */}
      <section className="survey-cta-banner">
        <div className="container">
          <div className="survey-cta-inner">
            <div className="survey-cta-content">
              <div className="survey-cta-badge">New</div>
              <h2>Neuroimmune &amp; Post-Infectious Conditions Survey</h2>
              <p>Help us understand health, lifestyle, symptoms, and function among people with complex chronic, post-infectious, and neuroimmune conditions. Your insights drive real research breakthroughs.</p>
              <div className="survey-cta-meta">
                <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> ~30 minutes</span>
                <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg> 100% Confidential</span>
                <span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg> IRB Approved</span>
              </div>
            </div>
            <div className="survey-cta-action">
              <Link to="/survey" className="btn-survey-start">
                Start Survey
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Feature cards */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Understanding the BioBank</h2>
            <p className="section-desc">Learn about how your samples contribute to groundbreaking medical research</p>
          </div>
          <div className="features-grid">
            {featureCards.map((card, i) => (
              <div className="feature-card" key={i} style={{ "--card-accent": card.color }}>
                <div className="feature-icon-title">
                  <div className="feature-icon-wrap" style={{ backgroundColor: card.color }}>
                    <img src={card.icon} alt="" className="feature-icon-img" />
                  </div>
                  <span className="feature-title">{card.title}</span>
                </div>
                <p className="feature-text">{card.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-desc">A simple, transparent process from consent to contribution</p>
          </div>
          <div className="steps-grid">
            {[
              { step: "01", title: "Review Information", desc: "Read the informed consent document to understand how your samples will be used." },
              { step: "02", title: "Complete the Form", desc: "Fill in your details and provide your digital signature below." },
              { step: "03", title: "Sample Collection", desc: "Your samples are collected during routine care with minimal discomfort." },
              { step: "04", title: "Advancing Research", desc: "De-identified samples help researchers discover new treatments and diagnostics." },
            ].map((s, i) => (
              <div className="step-card" key={i}>
                <div className="step-number">{s.step}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Consent Form */}
      <section className="consent-section" id="consent-form">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Consent Form</h2>
            <p className="section-desc">Please fill in your details below and provide your signature</p>
          </div>
          <form className="consent-form" onSubmit={onFormSubmit} autoComplete="off">
            <div className="form-grid">
              <div className="form-group">
                <label>Name of Participant<span className="req">*</span></label>
                <input value={patientName} onChange={(e) => setPatientName(e.target.value)} maxLength={100} placeholder="Enter full name" required />
              </div>
              <div className="form-group">
                <label>Date<span className="req">*</span></label>
                <input value={consentDate} onChange={(e) => setConsentDate(e.target.value)} placeholder="DD/MM/YYYY" required readOnly />
              </div>
              <div className="form-group">
                <label>Mobile No.<span className="req">*</span></label>
                <input value={patientMobile} onChange={(e) => setPatientMobile(e.target.value)} maxLength={12} placeholder="Enter mobile no" required />
              </div>
              <div className="form-group">
                <label>Email ID<span className="req">*</span></label>
                <input value={patientEmail} onChange={(e) => setPatientEmail(e.target.value)} maxLength={50} placeholder="Enter email id" required type="email" />
              </div>
            </div>

            <div className="consent-checkbox-row">
              <input type="checkbox" id="cbRead" checked={consentChecked} onChange={handleCheckboxChange} />
              <label htmlFor="cbRead">I have read Biobank Informed Consent.</label>
              <button type="button" className="link-btn" onClick={() => setModalOpen(true)}>View</button>
            </div>

            <div className="bio-specimens">
              <h3>I agree to Donate below Bio Specimens and provide list</h3>
              <ul className="specimens-list">
                {bioSpecimens.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>

            <div className="signature-section">
              <label>Signature of Participant</label>
              <div className="sig-canvas-wrap">
                <canvas ref={canvasRef} className="sig-canvas" width={1180} height={280}></canvas>
              </div>
              <div className="sig-toolbar">
                <button type="button" className="btn-sm-action" onClick={undoSignature}>Undo</button>
                <button type="button" className="btn-sm-action" onClick={clearSignature}>Clear</button>
                <button type="button" className="btn-sm-action" onClick={downloadSignature}>Download PNG</button>
                <span className="sig-help">Use mouse or touch to sign above.</span>
              </div>
            </div>

            <div className="form-grid cols-2">
              <div className="form-group">
                <label>Signature of participant (confirm)<span className="req">*</span></label>
                <input value={signatureOfParticipant} onChange={(e) => setSignatureOfParticipant(e.target.value)} maxLength={100} placeholder="Type full name as signature confirmation" required />
              </div>
              <div className="form-group">
                <label>Name of impartial witness</label>
                <input value={witnessName} onChange={(e) => setWitnessName(e.target.value)} maxLength={100} placeholder="Witness full name" />
              </div>
            </div>

            <div className="consent-confirm-row">
              <input type="checkbox" id="confirmConsent" checked={confirmConsent} onChange={(e) => setConfirmConsent(e.target.checked)} required />
              <label htmlFor="confirmConsent">
                I hereby acknowledge that I have carefully read and fully understood the Biobank Informed Consent document. I voluntarily and willingly agree to provide my biological samples and related data to the hospital's Biobank, with full awareness of its intended use.
              </label>
            </div>

            {/* Delegate section */}
            <div className="delegate-section">
              <h3>To be completed by the authorized delegate explaining consent</h3>
              <p>I confirm that the participant was given an opportunity to ask questions and all the questions asked by the participant have been answered correctly and to the best of my ability. I confirm that the individual hasn't been coerced into giving consent, and consent has been given freely and voluntarily.</p>
              <div className="form-grid cols-3">
                <div className="form-group">
                  <label>Name & signature of the person taking consent<span className="req">*</span></label>
                  <input value={personTakingConsent} onChange={(e) => setPersonTakingConsent(e.target.value)} placeholder="Full name" required />
                </div>
                <div className="form-group">
                  <label>Name of the Hospital / Institute<span className="req">*</span></label>
                  <select value={hospital} onChange={(e) => setHospital(e.target.value)} required>
                    <option value="">Select</option>
                    <option value="Continental Biobank">Continental Biobank</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Patient ID</label>
                  <input value={patientId} onChange={(e) => setPatientId(e.target.value)} placeholder="Hospital / MRN" />
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button className="btn-primary-action" type="submit" disabled={submitting}>
                {submitting ? "Submitting..." : "Submit Consent"}
              </button>
              <button className="btn-danger-action" type="button" onClick={() => handleSubmit(true)} disabled={submitting}>
                {submitting ? "Processing..." : "Submit & Print"}
              </button>
            </div>

            {formResult && (
              <div className={`form-alert ${formResult.type === "success" ? "alert-success" : "alert-error"}`}>
                {formResult.message}
              </div>
            )}
          </form>
        </div>
      </section>

      <ConsentModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        onAgree={handleModalAgree}
        language={getGoogleTranslateLang()}
      />
    </div>
  );
}
